import { useState, useEffect, useRef } from 'react';

/**
 * Multi-select dropdown filter with apply/reset actions
 * Used for filtering by location, user, status, etc.
 */
export function MultiSelectFilter({ label, items, selectedIds, onApply, getItemLabel, getItemId, pluralLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState(selectedIds);
  const dropdownRef = useRef(null);

  // Update temp selection when props change
  useEffect(() => {
    setTempSelected(selectedIds);
  }, [selectedIds]);

  // Handle click outside to cancel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setTempSelected(selectedIds); // Revert to previous state
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, selectedIds]);

  const toggleItem = (itemId) => {
    setTempSelected(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleApply = () => {
    onApply(tempSelected);
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempSelected([]);
    onApply([]);
    setIsOpen(false);
  };

  const getButtonLabel = () => {
    if (selectedIds.length === 0) {
      return label;
    } else if (selectedIds.length === 1) {
      const item = items.find(i => getItemId(i) === selectedIds[0]);
      return item ? getItemLabel(item) : label;
    } else {
      return `${selectedIds.length} ${pluralLabel} gewählt`;
    }
  };

  return (
    <div className="multi-select-filter" ref={dropdownRef}>
      <button
        className={`filter-toggle ${selectedIds.length > 0 ? 'has-selection' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {getButtonLabel()}
        <i className={`fas fa-chevron-down ${isOpen ? 'rotated' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="filter-dropdown">
          <div className="filter-options">
            {items.map(item => {
              const itemId = getItemId(item);
              const isChecked = tempSelected.includes(itemId);

              return (
                <label key={itemId} className="filter-option">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleItem(itemId)}
                  />
                  <span>{getItemLabel(item)}</span>
                </label>
              );
            })}
          </div>

          <div className="filter-actions">
            <button
              className="btn-secondary-small"
              onClick={handleReset}
            >
              Zurücksetzen
            </button>
            <button
              className="btn-primary-small"
              onClick={handleApply}
            >
              Anwenden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
